import type { Knex } from 'knex'
import type { AuditEntry, AuditQuery, AuditLogRow, AuditService } from './audit.types.js'

const TABLE = 'audit_log'

interface AuditServiceDeps {
  knex: Knex
  generateId: () => string
  nowTimestamp: (knex: Knex) => Knex.Raw | string
  logger: { warn: (msg: string, ...args: unknown[]) => void }
}

export function createAuditService(deps: AuditServiceDeps): AuditService {
  const { knex, generateId, nowTimestamp, logger } = deps

  function toRow(entry: AuditEntry): Record<string, unknown> {
    return {
      id: generateId(),
      source: entry.source,
      action: entry.action,
      actor_id: entry.actorId ?? null,
      actor_email: entry.actorEmail ?? null,
      resource_type: entry.resourceType ?? null,
      resource_id: entry.resourceId ?? null,
      ip_address: entry.ip ?? null,
      user_agent: entry.userAgent ?? null,
      metadata: entry.metadata ? JSON.stringify(entry.metadata) : null,
      created_at: nowTimestamp(knex)
    }
  }

  function log(entry: AuditEntry): void {
    knex(TABLE).insert(toRow(entry)).catch(() => {
      logger.warn(`Failed to persist audit event: ${entry.source}/${entry.action}`)
    })
  }

  function logBatch(entries: AuditEntry[]): void {
    if (entries.length === 0) return
    const rows = entries.map(toRow)
    knex(TABLE).insert(rows).catch(() => {
      logger.warn(`Failed to persist ${entries.length} audit events`)
    })
  }

  async function query(filters: AuditQuery): Promise<AuditLogRow[]> {
    let q = knex(TABLE).select('*')

    if (filters.source) q = q.where('source', filters.source)
    if (filters.action) q = q.where('action', filters.action)
    if (filters.actorId) q = q.where('actor_id', filters.actorId)
    if (filters.resourceType) q = q.where('resource_type', filters.resourceType)
    if (filters.resourceId) q = q.where('resource_id', filters.resourceId)
    if (filters.since) q = q.where('created_at', '>=', filters.since)
    if (filters.until) q = q.where('created_at', '<=', filters.until)

    q = q.orderBy('created_at', 'desc')
    if (filters.limit) q = q.limit(filters.limit)
    if (filters.offset) q = q.offset(filters.offset)

    return q as Promise<AuditLogRow[]>
  }

  return { log, logBatch, query }
}
